import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, useToast } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { dropzone } from 'helpers/makeFormFields';
import { defaultToastOptions } from 'helpers/manageErrorResponse';
import useFileUploadMutation from 'hooks/mutations/useFileUploadMutation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { useManuallyUploadPoaMutation } from '../queries/useManuallyUploadPoaMutation';

const schema = yup.object({
  power_of_attorney: yup.mixed<{ file: File }>().required(),
});

type Props = {
  verificationId: number | undefined;
  isOpen: boolean;
  onClose: () => void;
};

export const ManualPoaUploadModal = ({ verificationId, isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast({ ...defaultToastOptions });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { power_of_attorney: undefined },
  });

  const fileUploadMutation = useFileUploadMutation();
  const manuallyUploadPoaMutation = useManuallyUploadPoaMutation(verificationId);

  const isPending = fileUploadMutation.isPending || manuallyUploadPoaMutation.isPending;
  const pickedFile = watch('power_of_attorney');

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<yup.InferType<typeof schema>> = ({ power_of_attorney }) => {
    const formData = new FormData();
    formData.append('file', power_of_attorney.file);

    fileUploadMutation.mutate(formData, {
      onSuccess: (uploadedFile) => {
        manuallyUploadPoaMutation.mutate(
          { power_of_attorney_uuid: uploadedFile.data.uuid },
          {
            onSuccess: () => {
              toast({ title: t('workshop_verification_request:toast.success.poa_manually_uploaded'), status: 'success' });
              handleClose();
            },
            onError: (error) => {
              toast({ title: error.message ?? t('common:error'), status: 'error' });
            },
          },
        );
      },
    });
  };

  return (
    <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={handleClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('workshop_verification_request:manual_poa_upload.header')}</ModalHeader>
        <ModalCloseButton isDisabled={isPending} />
        <ModalBody pb={6}>
          <Text mb={4}>{t('workshop_verification_request:manual_poa_upload.description')}</Text>
          {dropzone({
            name: 'power_of_attorney',
            control,
            errors,
            disabled: isPending,
          })}
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClose} mr={3} variant="outline" isDisabled={isPending}>
            {t('common:cancel')}
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit(onSubmit)} isLoading={isPending} isDisabled={!pickedFile}>
            {t('workshop_verification_request:manual_poa_upload.confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
